import { Head } from '@inertiajs/react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import SyntaxHighlighter from 'react-syntax-highlighter'
import { atomOneDark } from 'react-syntax-highlighter/dist/esm/styles/hljs'
import { Image, Zap, AlertCircle, CheckCircle, Gauge } from "lucide-react"
import ImageResizeOptions from '@/components/ImageResizeOptions'

export default function ServingFiles() {
  const exampleImageUrl = "https://simple-file-upload76ax.files-simplefileupload.com/static/blobs/proxy/eyJfcmFpbHMiOnsibWVzc2FnZSI6IkJBaHBBbkpQIiwiZXhwIjpudWxsLCJwdXIiOiJibG9iX2lkIn19--6ba0119b4c09f9cb873ad1319f678dd2bbdde7a8/tunafish-mayonnaise-Fe9hvuoXny0-unsplash.jpg"
  
  return (
    <div className="container mx-auto py-6 px-4 max-w-6xl">
      <Head title="Serving Files - Documentation" />
      
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">Serving Files</h1>
        <p className="text-lg text-gray-600 mb-4">
          Learn how to use on-demand image resizing to optimize your images for different use cases and improve page load times.
        </p>
        
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <Gauge className="h-5 w-5 text-blue-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                <strong>TLDR:</strong> Add <code>?w=width&h=height</code> to any image URL to resize on-the-fly (e.g., <code>?w=200&h=200</code> for a 200x200 image). Use <code>&fit=fill</code> to control how images are cropped.
              </p>
            </div>
          </div>
        </div>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>What is On-Demand Image Resizing?</CardTitle>
          <CardDescription>
            Understanding the benefits of dynamic image optimization
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-gray-600">
            "On demand image resizing" means resizing and caching your images as they're requested. Have you ever seen an image tag like this?
          </p>
          
          <div className="bg-gray-50 rounded-lg p-4">
            <SyntaxHighlighter
              language="html"
              style={atomOneDark}
              customStyle={{
                background: '#21252b',
                padding: '1rem',
                margin: 0,
                fontSize: '.8rem',
                borderRadius: '6px'
              }}
            >
              {`<img src="image_url" width="400" />`}
            </SyntaxHighlighter>
          </div>

          <p className="text-gray-600">
            This tag is resizing the image in the browser with HTML. Why is this bad? Well, it's slow. And in the modern web era attention spans are short. You can't afford to have slow page load times.
          </p>

          <p className="text-gray-600">
            Resizing your images as they're requested and caching the transformed image is much faster.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start">
                <AlertCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5" />
                <div>
                  <h4 className="font-medium text-red-800 mb-1">Browser Resizing (Slow)</h4>
                  <ul className="text-sm text-red-700 space-y-1">
                    <li>• Downloads full-size image</li>
                    <li>• Uses browser CPU to resize</li>
                    <li>• Wastes bandwidth</li>
                    <li>• Slower page loads</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5" />
                <div>
                  <h4 className="font-medium text-green-800 mb-1">On-Demand Resizing (Fast)</h4>
                  <ul className="text-sm text-green-700 space-y-1">
                    <li>• Downloads optimized size</li>
                    <li>• Server-side processing</li>
                    <li>• Saves bandwidth</li>
                    <li>• Faster page loads</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>How Do I Resize On Demand?</CardTitle>
          <CardDescription>
            Simple URL parameters for instant image optimization
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-gray-600">
            Easy! Simply add query parameters for width(<code className="bg-gray-100 px-1 py-0.5 rounded">w</code>) and height(<code className="bg-gray-100 px-1 py-0.5 rounded">h</code>). Add <code className="bg-gray-100 px-2 py-1 rounded">?w=20&h=20</code> to the end of your image URL to resize to 20 by 20.
          </p>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-start">
              <Zap className="w-5 h-5 text-purple-500 mr-2 mt-0.5" />
              <div>
                <h4 className="font-medium text-purple-800 mb-1">Available Parameters</h4>
                <ul className="text-sm text-purple-700 space-y-1">
                  <li>• <code className="bg-purple-100 px-1 rounded">w</code> - Width in pixels</li>
                  <li>• <code className="bg-purple-100 px-1 rounded">h</code> - Height in pixels</li>
                  <li>• <code className="bg-purple-100 px-1 rounded">fit</code> - Resizing type (fit, fill, fill-down, force)</li>
                  <li>• Use both w & h for exact dimensions</li>
                  <li>• Use one dimension to maintain aspect ratio</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-amber-500 mr-2 mt-0.5" />
              <div>
                <h4 className="font-medium text-amber-800 mb-1">Aspect Ratio Preservation</h4>
                <p className="text-sm text-amber-700 mb-2">
                  Images are resized to fit within the specified dimensions while preserving their original aspect ratio. This prevents distortion.
                </p>
                <p className="text-sm text-amber-700">
                  <strong>Example:</strong> An image with original dimensions 2329×3409 resized with <code className="bg-amber-100 px-1 rounded">?w=200&h=200</code> will actually be 137×200 pixels, maintaining the original proportions.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Resizing Types (Fit Parameter)</CardTitle>
          <CardDescription>
            Control how images are resized with the fit parameter
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-gray-600">
            In addition to width and height, you can use the <code className="bg-gray-100 px-1 rounded">fit</code> parameter to control how images are resized. This parameter determines how the image should be adjusted when the requested dimensions don't match the original aspect ratio.
          </p>
          
          <p className="text-gray-600 text-sm">
            <strong>Note:</strong> Append these query parameters to your image URL. For example: <code className="bg-gray-100 px-1 rounded">your-image-url.jpg?w=300&h=200&fit=fill</code>
          </p>
          
          <div className="space-y-4">
            <div className="border-l-4 border-blue-500 pl-4">
              <h4 className="font-semibold text-gray-800 mb-1">fit (default)</h4>
              <p className="text-gray-600 text-sm mb-2">
                Resizes the image while keeping aspect ratio to fit within the given dimensions. The entire image will be visible, but may have empty space if aspect ratios don't match.
              </p>
              <div className="bg-gray-50 rounded p-2">
                <code className="text-sm">?w=300&h=200&fit=fit</code>
              </div>
            </div>
            
            <div className="border-l-4 border-green-500 pl-4">
              <h4 className="font-semibold text-gray-800 mb-1">fill</h4>
              <p className="text-gray-600 text-sm mb-2">
                Resizes the image while keeping aspect ratio to fill the given dimensions completely. Parts of the image may be cropped if aspect ratios don't match.
              </p>
              <div className="bg-gray-50 rounded p-2">
                <code className="text-sm">?w=300&h=200&fit=fill</code>
              </div>
            </div>
            
            <div className="border-l-4 border-purple-500 pl-4">
              <h4 className="font-semibold text-gray-800 mb-1">fill-down</h4>
              <p className="text-gray-600 text-sm mb-2">
                Same as fill, but if the resized image is smaller than the requested size, it will crop the result to maintain the requested aspect ratio.
              </p>
              <div className="bg-gray-50 rounded p-2">
                <code className="text-sm">?w=300&h=200&fit=fill-down</code>
              </div>
            </div>
            
            <div className="border-l-4 border-orange-500 pl-4">
              <h4 className="font-semibold text-gray-800 mb-1">force</h4>
              <p className="text-gray-600 text-sm mb-2">
                Resizes the image to exact dimensions without keeping the aspect ratio. This may distort the image but guarantees exact dimensions.
              </p>
              <div className="bg-gray-50 rounded p-2">
                <code className="text-sm">?w=300&h=200&fit=force</code>
              </div>
            </div>
          </div>
          
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-yellow-600 mr-2 mt-0.5" />
              <div>
                <h4 className="font-medium text-yellow-800 mb-1">Usage Tips</h4>
                <ul className="text-sm text-yellow-700 space-y-1">
                  <li>• Use <strong>fit</strong> for thumbnails where you need the entire image visible</li>
                  <li>• Use <strong>fill</strong> for hero images or backgrounds where consistent dimensions are important</li>
                  <li>• Use <strong>force</strong> sparingly as it can distort images</li>
                  <li>• Combine with width and height for precise control</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Live Example</CardTitle>
          <CardDescription>
            Try resizing an image with our interactive tool
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-gray-600">
            Here is an image saved to Simple File Upload storage at full resolution:
          </p>
          
          <div className="mb-4">
            <a href={exampleImageUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm">
              Click to open original image in new tab →
            </a>
          </div>

          <p className="text-gray-600 mb-4">
            Use the interactive tool below to resize the image in real-time and see how different parameters affect the output:
          </p>

          <ImageResizeOptions imageUrl={exampleImageUrl} />
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Code Examples</CardTitle>
          <CardDescription>
            How to implement resized images in your application
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-4">
            <div>
              <h4 className="font-medium mb-2">HTML</h4>
              <SyntaxHighlighter
                language="html"
                style={atomOneDark}
                customStyle={{
                  background: '#21252b',
                  padding: '1rem',
                  margin: 0,
                  fontSize: '.8rem',
                  borderRadius: '6px'
                }}
              >
                {`<!-- Thumbnail (100x100) -->
<img src="https://your-cdn-url.com/image.jpg?w=100&h=100" alt="Thumbnail" />

<!-- Fixed width, auto height -->
<img src="https://your-cdn-url.com/image.jpg?w=800" alt="Medium image" />

<!-- Responsive images -->
<picture>
  <source media="(max-width: 640px)" srcset="https://your-cdn-url.com/image.jpg?w=640">
  <source media="(max-width: 1024px)" srcset="https://your-cdn-url.com/image.jpg?w=1024">
  <img src="https://your-cdn-url.com/image.jpg?w=1920" alt="Responsive image">
</picture>`}
              </SyntaxHighlighter>
            </div>

            <div>
              <h4 className="font-medium mb-2">JavaScript</h4>
              <SyntaxHighlighter
                language="javascript"
                style={atomOneDark}
                customStyle={{
                  background: '#21252b',
                  padding: '1rem',
                  margin: 0,
                  fontSize: '.8rem',
                  borderRadius: '6px'
                }}
              >
                {`// Dynamic image resizing
function getResizedImageUrl(originalUrl, width, height) {
  const params = new URLSearchParams();
  if (width) params.append('w', width);
  if (height) params.append('h', height);
  return \`\${originalUrl}?\${params.toString()}\`;
}

// Usage
const thumbnail = getResizedImageUrl(cdnUrl, 150, 150);
const hero = getResizedImageUrl(cdnUrl, 1920, 1080);`}
              </SyntaxHighlighter>
            </div>

            <div>
              <h4 className="font-medium mb-2">React</h4>
              <SyntaxHighlighter
                language="jsx"
                style={atomOneDark}
                customStyle={{
                  background: '#21252b',
                  padding: '1rem',
                  margin: 0,
                  fontSize: '.8rem',
                  borderRadius: '6px'
                }}
              >
                {`// React component with responsive images
function ResponsiveImage({ src, alt }) {
  return (
    <picture>
      <source 
        media="(max-width: 640px)" 
        srcSet={\`\${src}?w=640\`} 
      />
      <source 
        media="(max-width: 1024px)" 
        srcSet={\`\${src}?w=1024\`} 
      />
      <img 
        src={\`\${src}?w=1920\`} 
        alt={alt}
        loading="lazy"
      />
    </picture>
  );
}`}
              </SyntaxHighlighter>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Performance Benefits</CardTitle>
          <CardDescription>
            Why on-demand resizing improves your application
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4">
              <div className="text-3xl font-bold text-green-600 mb-2">70%</div>
              <div className="text-sm text-gray-600">Average bandwidth savings</div>
            </div>
            <div className="text-center p-4">
              <div className="text-3xl font-bold text-blue-600 mb-2">3x</div>
              <div className="text-sm text-gray-600">Faster page load times</div>
            </div>
            <div className="text-center p-4">
              <div className="text-3xl font-bold text-purple-600 mb-2">∞</div>
              <div className="text-sm text-gray-600">Cached variations</div>
            </div>
          </div>
          
          <div className="mt-6 space-y-3">
            <div className="flex items-start">
              <Image className="w-5 h-5 mr-2 mt-0.5 text-blue-500" />
              <div>
                <strong>Automatic format optimization:</strong> Images are served in the most efficient format for each browser
              </div>
            </div>
            <div className="flex items-start">
              <Image className="w-5 h-5 mr-2 mt-0.5 text-blue-500" />
              <div>
                <strong>Global CDN delivery:</strong> Resized images are cached and served from edge locations worldwide
              </div>
            </div>
            <div className="flex items-start">
              <Image className="w-5 h-5 mr-2 mt-0.5 text-blue-500" />
              <div>
                <strong>No storage overhead:</strong> Variations are generated on-demand, not pre-stored
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Best Practices</CardTitle>
          <CardDescription>
            Tips for implementing image resizing effectively
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="space-y-3">
            <li className="flex items-start">
              <CheckCircle className="w-5 h-5 mr-2 mt-0.5 text-green-500" />
              <div>
                <strong>Use responsive images:</strong> Serve different sizes for different screen sizes using the <code className="bg-gray-100 px-1 rounded">picture</code> element
              </div>
            </li>
            <li className="flex items-start">
              <CheckCircle className="w-5 h-5 mr-2 mt-0.5 text-green-500" />
              <div>
                <strong>Lazy load images:</strong> Add <code className="bg-gray-100 px-1 rounded">loading="lazy"</code> to images below the fold
              </div>
            </li>
            <li className="flex items-start">
              <CheckCircle className="w-5 h-5 mr-2 mt-0.5 text-green-500" />
              <div>
                <strong>Maintain aspect ratios:</strong> Use only width or height parameter to preserve original proportions
              </div>
            </li>
            <li className="flex items-start">
              <CheckCircle className="w-5 h-5 mr-2 mt-0.5 text-green-500" />
              <div>
                <strong>Consider retina displays:</strong> Serve 2x resolution images for high-DPI screens
              </div>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  )
}
